import React, { useState } from 'react';
import { Card, Button } from '../ui';

function LeagueCard({ league, canJoin, onJoin }) {
    const [isJoining, setIsJoining] = useState(false);

    const handleJoinClick = async () => {
        if (!canJoin || league.status !== 'OPEN' || league.currentTeams >= league.maxTeams) {
            return;
        }

        setIsJoining(true);
        try {
            await onJoin(league.id);
        } catch (error) {
            console.error('Error joining league:', error);
        } finally {
            setIsJoining(false);
        }
    };

    const getStatusInfo = () => {
        if (league.status === 'OPEN') {
            if (league.currentTeams >= league.maxTeams) {
                return { text: 'FULL', color: 'var(--color-danger)', canJoin: false };
            }
            return { text: 'OPEN', color: 'var(--color-success)', canJoin: true };
        } else if (league.status === 'LOCKED') {
            return { text: 'LOCKED', color: 'var(--color-warning)', canJoin: false };
        } else {
            return { text: 'COMPLETED', color: 'var(--color-text-secondary)', canJoin: false };
        }
    };

    const getLeagueTypeInfo = () => {
        if (league.leagueType === 'PUBLIC') {
            return { text: 'PUBLIC', icon: '🌐' };
        } else {
            return { text: 'PRIVATE', icon: '🔒' };
        }
    };

    const statusInfo = getStatusInfo();
    const typeInfo = getLeagueTypeInfo();
    const spotsRemaining = league.maxTeams - league.currentTeams;
    const canJoinLeague = statusInfo.canJoin && canJoin;

    // Badge content
    const badge = (
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2xs)' }}>
            <span>{typeInfo.icon}</span>
            {typeInfo.text}
        </span>
    );

    // Button text based on state
    const getButtonText = () => {
        if (isJoining) return 'Joining...';
        if (canJoinLeague) return 'Join League';
        if (!canJoin) return 'Cannot Join';
        if (statusInfo.text === 'FULL') return 'League Full';
        return 'Not Available';
    };

    return (
        <Card
            variant={canJoinLeague ? 'interactive' : 'default'}
            hoverable={canJoinLeague}
            badge={badge}
            footer={
                <Button
                    variant="primary"
                    fullWidth
                    onClick={handleJoinClick}
                    disabled={!canJoinLeague || isJoining}
                    isLoading={isJoining}
                >
                    {getButtonText()}
                </Button>
            }
        >
            {/* League Name */}
            <h3 className="card__title" style={{ marginRight: 'var(--spacing-2xl)' }}>
                {league.name}
            </h3>

            {/* Status */}
            <div className="card__meta">
                <span style={{ color: 'var(--color-text-secondary)' }}>Status:</span>
                <span style={{
                    color: statusInfo.color,
                    fontWeight: 'var(--font-weight-bold)'
                }}>
                    {statusInfo.text}
                </span>
            </div>

            {/* Teams Info */}
            <div className="card__meta">
                <span style={{ color: 'var(--color-text-secondary)' }}>Teams:</span>
                <span style={{ fontWeight: 'var(--font-weight-bold)' }}>
                    {league.currentTeams}/{league.maxTeams}
                </span>
                {spotsRemaining > 0 && spotsRemaining <= 3 && (
                    <span style={{
                        color: 'var(--color-warning)',
                        fontSize: 'var(--font-size-sm)',
                        fontStyle: 'italic'
                    }}>
                        ({spotsRemaining} spots left)
                    </span>
                )}
            </div>

            {/* Entry Fee
              *
              * Three distinct states, never two. `entryFee > 0 ? fee : 'FREE'`
              * collapsed "unknown" into "FREE" — an unknown fee is NOT a promise
              * of free entry, and the backend charges league.get_entry_cost()
              * whatever this card claimed. Only a real number is priced; a
              * non-number renders an explicit unknown marker.
              */}
            <div className="card__meta">
                <span style={{ color: 'var(--color-text-secondary)' }}>Entry:</span>
                <span style={{
                    color: typeof league.entryFee !== 'number'
                        ? 'var(--color-text-tertiary)'
                        : league.entryFee > 0
                            ? 'var(--color-warning)'
                            : 'var(--color-success)',
                    fontWeight: 'var(--font-weight-bold)'
                }}>
                    {typeof league.entryFee !== 'number'
                        ? '—'
                        : league.entryFee > 0
                            ? `$${league.entryFee}`
                            : 'FREE'}
                </span>
                {league.prizePool > 0 && (
                    <span style={{
                        color: 'var(--color-warning)',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        (Prize: ${league.prizePool})
                    </span>
                )}
            </div>

            {/* Creation Date */}
            <div style={{
                marginTop: 'var(--spacing-sm)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)',
                textAlign: 'center'
            }}>
                Created: {new Date(league.createdAt).toLocaleDateString()}
            </div>
        </Card>
    );
}

export default LeagueCard;